import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload, Download, FileText, ArrowRight, Check, X, Loader2,
  AlertCircle, ChevronRight, Database, Users, Briefcase, FileSpreadsheet,
} from 'lucide-react';

const ImportExportPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('import');

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto animate-fadeIn" data-testid="import-export-page">
        <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Import & Export</h1>
        <p className="text-sm text-slate-500 mb-6">Bulk import data from CSV/Excel or export your data</p>

        <div className="flex gap-2 mb-6">
          <Button variant={activeTab === 'import' ? 'default' : 'outline'} onClick={() => setActiveTab('import')} data-testid="tab-import">
            <Upload className="w-4 h-4 mr-2" />Import Data
          </Button>
          <Button variant={activeTab === 'export' ? 'default' : 'outline'} onClick={() => setActiveTab('export')} data-testid="tab-export">
            <Download className="w-4 h-4 mr-2" />Export Data
          </Button>
        </div>

        {activeTab === 'import' && <ImportSection token={token} />}
        {activeTab === 'export' && <ExportSection token={token} />}
      </div>
    </DashboardLayout>
  );
};

const ImportSection = ({ token }) => {
  const fileRef = useRef(null);
  const cvRef = useRef(null);
  const [entity, setEntity] = useState('candidates');
  const [uploading, setUploading] = useState(false);
  const [session, setSession] = useState(null);
  const [mappings, setMappings] = useState({});
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadingCvs, setUploadingCvs] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setResult(null); setSession(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/import/upload?entity=${entity}`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }, withCredentials: true,
      });
      setSession(res.data);
      const auto = {};
      for (const [src, tgt] of Object.entries(res.data.auto_mapping || {})) {
        auto[src] = tgt;
      }
      setMappings(auto);
      toast.success(`File parsed: ${res.data.row_count} rows found`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleExecute = async () => {
    if (!session) return;
    setExecuting(true);
    try {
      const res = await axios.post(`${API}/import/execute/${session.session_id}`, { field_mapping: mappings }, {
        headers, withCredentials: true, timeout: 120000,
      });
      setResult(res.data);
      toast.success(`Import complete: ${res.data.imported} imported, ${res.data.failed} errors`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setExecuting(false);
  };

  const handleCvUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !result) return;
    setUploadingCvs(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      const res = await axios.post(`${API}/import/cv-match/${session.session_id}`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }, withCredentials: true,
      });
      toast.success(`CVs matched: ${res.data.matched}, unmatched: ${res.data.unmatched}`);
    } catch { toast.error('CV upload failed'); }
    setUploadingCvs(false);
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Upload */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4 text-indigo-600" />Step 1: Upload File</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div>
              <Label>Data Type</Label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger className="w-48 mt-1" data-testid="import-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="candidates">Candidates</SelectItem>
                  <SelectItem value="jobs">Jobs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleUpload} className="hidden" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Parsing...</> : <><FileSpreadsheet className="w-4 h-4 mr-2" />Upload CSV / Excel</>}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-400">If your file has different column names, we'll suggest mappings. You can adjust before importing.</p>
        </CardContent>
      </Card>

      {/* Step 2: Field Mapping */}
      {session && !result && (
        <Card data-testid="field-mapping-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><ArrowRight className="w-4 h-4 text-indigo-600" />Step 2: Map Fields</CardTitle>
            <p className="text-sm text-slate-500">{session.row_count} rows found. Map your columns to EdAssist fields.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="bg-slate-50 rounded-lg p-3 border">
              <div className="grid grid-cols-[1fr_40px_1fr] gap-2 text-xs font-medium text-slate-500 mb-2">
                <span>Your Column</span><span></span><span>EdAssist Field</span>
              </div>
              {session.source_fields.map(src => {
                return (
                  <div key={src} className="grid grid-cols-[1fr_40px_1fr] gap-2 items-center py-1.5 border-b last:border-0">
                    <span className="text-sm font-medium text-slate-700 truncate">{src}</span>
                    <ChevronRight className="w-4 h-4 text-slate-300 mx-auto" />
                    <Select value={mappings[src] || '__skip__'} onValueChange={(v) => setMappings(m => ({ ...m, [src]: v === '__skip__' ? '' : v }))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Skip" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__skip__">— Skip —</SelectItem>
                        {session.target_fields.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>

            {/* Preview */}
            {session.preview?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">Preview (first {session.preview.length} rows)</p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="text-xs w-full">
                    <thead className="bg-slate-50"><tr>{session.source_fields.map(f => <th key={f} className="px-2 py-1.5 text-left font-medium text-slate-500 whitespace-nowrap">{f}</th>)}</tr></thead>
                    <tbody>{session.preview.map((row, i) => (
                      <tr key={i} className="border-t">{session.source_fields.map(f => <td key={f} className="px-2 py-1 text-slate-700 whitespace-nowrap max-w-[150px] truncate">{row[f]}</td>)}</tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}

            <Button onClick={handleExecute} disabled={executing} className="w-full" data-testid="execute-import-btn">
              {executing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : <><Database className="w-4 h-4 mr-2" />Import {session.row_count} Records</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Result + CV Upload */}
      {result && (
        <Card data-testid="import-result-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Check className="w-4 h-4 text-green-600" />Import Complete</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-green-50 rounded-lg"><p className="text-2xl font-bold text-green-700">{result.imported}</p><p className="text-xs text-green-600">Imported</p></div>
              <div className="text-center p-3 bg-red-50 rounded-lg"><p className="text-2xl font-bold text-red-700">{result.failed}</p><p className="text-xs text-red-600">Failed</p></div>
              <div className="text-center p-3 bg-slate-50 rounded-lg"><p className="text-2xl font-bold text-slate-700">{result.total}</p><p className="text-xs text-slate-600">Total</p></div>
            </div>
            {result.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                <p className="text-xs font-medium text-red-700 mb-1">Errors:</p>
                {result.errors.slice(0, 10).map((e, i) => <p key={i} className="text-xs text-red-600">Row {e.row}: {e.error}</p>)}
              </div>
            )}
            {entity === 'candidates' && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Step 3: Upload CVs (Optional)</p>
                <p className="text-xs text-slate-500 mb-3">Upload CV files and we'll auto-match them to candidates by name in the filename.</p>
                <input ref={cvRef} type="file" accept=".pdf,.docx,.txt" multiple onChange={handleCvUpload} className="hidden" />
                <Button variant="outline" onClick={() => cvRef.current?.click()} disabled={uploadingCvs} data-testid="upload-cvs-btn">
                  {uploadingCvs ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Matching CVs...</> : <><Upload className="w-4 h-4 mr-2" />Upload CVs</>}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const ExportSection = ({ token }) => {
  const [exporting, setExporting] = useState(null);
  const [search, setSearch] = useState('');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const handleExport = async (entity, format = 'csv') => {
    setExporting(`${entity}_${format}`);
    try {
      if (format === 'xlsx') {
        // Direct download via browser — backend returns file stream
        const params = new URLSearchParams({ format: 'xlsx' });
        if (search) params.append('search', search);
        const url = `${API}/export/${entity}?${params}`;
        const res = await fetch(url, { headers, credentials: 'include' });
        if (!res.ok) throw new Error('Export failed');
        const blob = await res.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `edassist_${entity}_export.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success(`Exported ${entity} as Excel`);
      } else {
        const params = search ? { search } : {};
        const res = await axios.get(`${API}/export/${entity}`, { headers, params, withCredentials: true });
        const data = res.data.data || [];
        if (!data.length) { toast.error('No data to export'); setExporting(null); return; }

        const allKeys = [...new Set(data.flatMap(r => Object.keys(r)))].filter(k => !['_id', 'password_hash', 'resume_text', 'parsed_data'].includes(k));
        const csvRows = [allKeys.join(',')];
        for (const row of data) {
          csvRows.push(allKeys.map(k => {
            let v = row[k];
            if (Array.isArray(v)) v = v.map(i => typeof i === 'object' ? JSON.stringify(i) : i).join('; ');
            else if (typeof v === 'object' && v !== null) v = JSON.stringify(v);
            v = String(v ?? '').replace(/"/g, '""');
            return v.includes(',') || v.includes('\n') ? `"${v}"` : v;
          }).join(','));
        }
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `edassist_${entity}_export.csv`; a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${data.length} ${entity}`);
      }
    } catch { toast.error('Export failed'); }
    setExporting(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end mb-4">
        <div className="flex-1"><Label>Search Filter (optional)</Label><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter by name..." className="mt-1" data-testid="export-search" /></div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { entity: 'candidates', label: 'Candidates', desc: 'Profiles, skills, education, experience', icon: Users, color: 'indigo' },
          { entity: 'jobs', label: 'Jobs', desc: 'All job postings with details', icon: Briefcase, color: 'green' },
          { entity: 'applications', label: 'Applications', desc: 'All applications with status', icon: FileText, color: 'purple' },
        ].map(item => (
          <Card key={item.entity} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6 text-center space-y-3">
              <div className={`w-12 h-12 bg-${item.color}-100 rounded-xl flex items-center justify-center mx-auto`}>
                <item.icon className={`w-6 h-6 text-${item.color}-600`} />
              </div>
              <h3 className="font-semibold text-slate-900">{item.label}</h3>
              <p className="text-xs text-slate-500">{item.desc}</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" disabled={!!exporting} onClick={() => handleExport(item.entity, 'csv')} data-testid={`export-${item.entity}-csv-btn`}>
                  {exporting === `${item.entity}_csv` ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  CSV
                </Button>
                <Button size="sm" disabled={!!exporting} onClick={() => handleExport(item.entity, 'xlsx')} data-testid={`export-${item.entity}-xlsx-btn`}>
                  {exporting === `${item.entity}_xlsx` ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  Excel
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ImportExportPage;
